import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  EllipsisVerticalIcon,
  ClipboardIcon,
  TrashIcon,
  ArrowTopRightOnSquareIcon,
  CalendarIcon,
  ChartBarIcon,
  PlayIcon,
  ShareIcon,
} from '@heroicons/react/24/outline'
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid'
import { HeartIcon as HeartIconOutline } from '@heroicons/react/24/outline'
import SecurityBadges from '../SecurityBadges'
import FolderBreadcrumb from '../FolderBreadcrumb'
import YouTubeVideoModal from './YouTubeVideoModal'
import { extractYouTubeVideoId } from '@/utils/urlUtils'
import toast from 'react-hot-toast'
import { bookmarkApi } from '../../../../shared/services/bookmarkApi'
import type { BookmarkedUrl } from '../../../../types/bookmark'
import { useTranslation } from 'react-i18next'
import { formatDate } from '../../../../shared/lib/format'

interface MobileUrlCardProps {
  url: BookmarkedUrl
  onUpdate: (url: BookmarkedUrl) => void
  onDelete: (urlId: string) => void
  onCopySuccess: (text: string) => void
  onTagClick?: (tagId: string) => void
}

/** 좁은 화면 전용 카드 — 터치 타깃과 액션 시트를 별도로 구성한다 */
export default function MobileUrlCard({
  url,
  onUpdate,
  onDelete,
  onCopySuccess,
  onTagClick,
}: MobileUrlCardProps) {
  const { t } = useTranslation()
  const [showActions, setShowActions] = useState(false)
  const [showVideoModal, setShowVideoModal] = useState(false)

  // 폴더 클릭 핸들러
  const handleFolderClick = (folderId: string) => {
    const event = new CustomEvent('folderNavigate', {
      detail: { folderId },
    })
    window.dispatchEvent(event)
  }

  // YouTube 비디오 관련 변수
  const isVideo =
    url.originalUrl.includes('youtube.com') ||
    url.originalUrl.includes('youtu.be')
  const youtubeVideoId = isVideo ? extractYouTubeVideoId(url.originalUrl) : null

  const handleToggleFavorite = async () => {
    try {
      const isFavorite = await bookmarkApi.toggleFavorite(url._id)
      onUpdate({ ...url, isFavorite })
    } catch (error) {
      toast.error(t('link.favoriteError'))
      console.error('Failed to toggle favorite:', error)
    }
    setShowActions(false)
  }

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: url.title || 'Shortened URL',
          text: `Check out this link: ${url.title || 'Shortened URL'}`,
          url: url.shortUrl,
        })
      } catch (err) {
        console.error('Error sharing:', err)
        // Fallback to copy
        navigator.clipboard.writeText(url.shortUrl)
        onCopySuccess(url.shortUrl)
      }
    } else {
      // Fallback to copy
      navigator.clipboard.writeText(url.shortUrl)
      onCopySuccess(url.shortUrl)
    }
    setShowActions(false)
  }

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url.shortUrl)
      onCopySuccess(url.shortUrl)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
    setShowActions(false)
  }

  const handleOpenOriginal = () => {
    window.open(url.originalUrl, '_blank', 'noopener,noreferrer')
    setShowActions(false)
  }

  // URL 타입 판별 함수
  const getUrlType = (originalUrl: string) => {
    if (
      originalUrl.includes('youtube.com') ||
      originalUrl.includes('youtu.be')
    ) {
      return {
        type: 'YouTube',
        icon: '📺',
        color: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400',
        isVideo: true,
      }
    }
    if (originalUrl.includes('instagram.com')) {
      return {
        type: 'Instagram',
        icon: '📷',
        color:
          'bg-pink-100 text-pink-700 dark:bg-pink-900/20 dark:text-pink-400',
      }
    }
    if (
      originalUrl.includes('facebook.com') ||
      originalUrl.includes('fb.com')
    ) {
      return {
        type: 'Facebook',
        icon: '👥',
        color:
          'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
      }
    }
    if (originalUrl.includes('twitter.com') || originalUrl.includes('x.com')) {
      return {
        type: 'Twitter/X',
        icon: '🐦',
        color:
          'bg-gray-100 text-gray-700 dark:bg-gray-700/20 dark:text-gray-400',
      }
    }
    if (originalUrl.includes('linkedin.com')) {
      return {
        type: 'LinkedIn',
        icon: '💼',
        color:
          'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
      }
    }
    if (originalUrl.includes('tiktok.com')) {
      return {
        type: 'TikTok',
        icon: '🎵',
        color:
          'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400',
      }
    }
    if (originalUrl.includes('github.com')) {
      return {
        type: 'GitHub',
        icon: '⚡',
        color:
          'bg-gray-100 text-gray-700 dark:bg-gray-700/20 dark:text-gray-400',
      }
    }
    if (
      originalUrl.includes('notion.so') ||
      originalUrl.includes('notion.site')
    ) {
      return {
        type: 'Notion',
        icon: '📝',
        color:
          'bg-gray-100 text-gray-700 dark:bg-gray-700/20 dark:text-gray-400',
      }
    }
    if (originalUrl.includes('google.com')) {
      return {
        type: 'Google',
        icon: '🔍',
        color:
          'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
      }
    }
    if (
      originalUrl.includes('amazon.com') ||
      originalUrl.includes('coupang.com')
    ) {
      return {
        type: 'Shopping',
        icon: '🛒',
        color:
          'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400',
      }
    }
    // 기본값
    return {
      type: 'Website',
      icon: '🌐',
      color: 'bg-gray-100 text-gray-600 dark:bg-gray-700/20 dark:text-gray-400',
    }
  }

  const urlTypeInfo = getUrlType(url.originalUrl)

  const handlePlayVideo = () => {
    if (youtubeVideoId) {
      setShowVideoModal(true)
    }
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      {/* 카드 헤더 */}
      <div className="border-b border-gray-100 p-5 dark:border-gray-700">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {/* URL 타입 배지 */}
            <div className="mb-3 flex items-center gap-2">
              <span
                className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium ${urlTypeInfo.color}`}
              >
                <span className="mr-1.5">{urlTypeInfo.icon}</span>
                {urlTypeInfo.type}
              </span>
              {/* YouTube 플레이 버튼 */}
              {urlTypeInfo.isVideo && youtubeVideoId && (
                <button
                  onClick={handlePlayVideo}
                  className="inline-flex items-center rounded-full bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700 active:scale-95"
                  title={t('link.playVideo')}
                >
                  <PlayIcon className="mr-1 h-3 w-3" />
                  {t('link.play')}
                </button>
              )}
              {url.isFavorite && (
                <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700 dark:bg-red-900/20 dark:text-red-400">
                  ❤️ 즐겨찾기
                </span>
              )}
            </div>

            {/* 보안 배지 */}
            <SecurityBadges url={url} className="mb-3" />

            {/* 제목 */}
            <div className="mb-3 flex items-start gap-2">
              {url.favicon && (
                <img
                  src={url.favicon}
                  alt=""
                  className="mt-0.5 h-5 w-5 flex-shrink-0 rounded"
                  onError={e => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              )}
              <h3 className="text-base font-semibold leading-snug text-gray-900 dark:text-white">
                {url.title || '제목 없음'}
              </h3>
            </div>

            {/* 단축 URL */}
            <div className="mb-3 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-blue-600 dark:text-blue-400">
                    단축 URL
                  </p>
                  <code className="block truncate font-mono text-sm text-blue-800 dark:text-blue-300">
                    {url.shortUrl}
                  </code>
                </div>
                <button
                  onClick={handleCopyUrl}
                  className="ml-3 rounded-lg bg-blue-600 p-2.5 text-white shadow-sm transition-colors hover:bg-blue-700 active:scale-95"
                  title={t('link.copyUrl')}
                >
                  <ClipboardIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* 원본 URL 미리보기 */}
            <div className="truncate text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium">원본:</span> {url.originalUrl}
            </div>
          </div>

          {/* 모바일 액션 버튼 */}
          <button
            onClick={() => setShowActions(!showActions)}
            className="flex-shrink-0 rounded-lg p-3 transition-colors hover:bg-gray-100 active:scale-95 dark:hover:bg-gray-700"
          >
            <EllipsisVerticalIcon className="h-6 w-6 text-gray-400" />
          </button>
        </div>

        {/* 태그들 */}
        {url.tags.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {url.tags.slice(0, 4).map(tag => (
              <button
                key={tag._id}
                onClick={() => onTagClick?.(tag._id)}
                className="inline-flex min-h-[32px] items-center rounded-full px-3 py-1.5 text-xs font-medium transition-opacity hover:opacity-80 active:scale-95"
                style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
                title={`"${tag.name}" 태그로 필터링`}
              >
                #{tag.name}
              </button>
            ))}
            {url.tags.length > 4 && (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                +{url.tags.length - 4}개
              </span>
            )}
          </div>
        )}

        {/* 메모 */}
        {url.notes && (
          <div className="mt-4 rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50">
            <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
              {url.notes}
            </p>
          </div>
        )}
      </div>

      {/* 확장 가능한 액션 영역 */}
      <AnimatePresence>
        {showActions && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="dark:bg-gray-750 border-t border-gray-100 bg-gray-50 dark:border-gray-700"
          >
            <div className="grid grid-cols-2 gap-3 p-4">
              {/* 링크 열기 */}
              <button
                onClick={handleOpenOriginal}
                className="flex items-center justify-center rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-colors hover:bg-gray-50 active:scale-95 dark:border-gray-500 dark:bg-gray-600 dark:hover:bg-gray-500"
              >
                <ArrowTopRightOnSquareIcon className="mr-2 h-5 w-5 text-gray-600 dark:text-gray-300" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('link.open')}
                </span>
              </button>

              {/* 공유 버튼 */}
              <button
                onClick={handleNativeShare}
                className="flex items-center justify-center rounded-lg bg-blue-600 p-3 text-white shadow-sm transition-colors hover:bg-blue-700 active:scale-95"
              >
                <ShareIcon className="mr-2 h-5 w-5" />
                <span className="text-sm font-medium">공유하기</span>
              </button>

              {/* 즐겨찾기 토글 */}
              <button
                onClick={handleToggleFavorite}
                className={`flex items-center justify-center rounded-lg border p-3 shadow-sm transition-colors active:scale-95 ${
                  url.isFavorite
                    ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                    : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-500 dark:bg-gray-600 dark:hover:bg-gray-500'
                }`}
              >
                {url.isFavorite ? (
                  <HeartIconSolid className="mr-2 h-5 w-5 text-red-500" />
                ) : (
                  <HeartIconOutline className="mr-2 h-5 w-5 text-gray-600 dark:text-gray-300" />
                )}
                <span
                  className={`text-sm font-medium ${
                    url.isFavorite
                      ? 'text-red-700 dark:text-red-400'
                      : 'text-gray-700 dark:text-gray-200'
                  }`}
                >
                  {url.isFavorite ? '즐겨찾기 해제' : '즐겨찾기'}
                </span>
              </button>

              {/* 삭제 버튼 */}
              <button
                onClick={() => {
                  onDelete(url._id)
                  setShowActions(false)
                }}
                className="flex items-center justify-center rounded-lg border border-red-200 bg-red-50 p-3 shadow-sm transition-colors hover:bg-red-100 active:scale-95 dark:border-red-800 dark:bg-red-900/20 dark:hover:bg-red-900/30"
              >
                <TrashIcon className="mr-2 h-5 w-5 text-red-500" />
                <span className="text-sm font-medium text-red-700 dark:text-red-400">
                  {t('link.delete')}
                </span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 통계 정보 */}
      <div className="dark:bg-gray-750 flex items-center justify-between bg-gray-50 px-5 py-4">
        <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center">
            <ChartBarIcon className="mr-1.5 h-4 w-4" />
            <span className="font-medium">
              {url.clickCount.toLocaleString()}
            </span>
            <span className="ml-1">클릭</span>
          </div>
          <div className="flex items-center">
            <CalendarIcon className="mr-1.5 h-4 w-4" />
            <span>{formatDate(url.createdAt)}</span>
          </div>
        </div>

        {/* 폴더 정보 - 계층 구조 표시 */}
        {url.folderId && (
          <div className="flex items-center">
            <FolderBreadcrumb
              folder={url.folderId}
              onFolderClick={handleFolderClick}
              className="text-gray-500 dark:text-gray-400"
            />
          </div>
        )}
      </div>

      {/* YouTube 비디오 모달 */}
      <AnimatePresence>
        {showVideoModal && youtubeVideoId && (
          <YouTubeVideoModal
            videoId={youtubeVideoId}
            title={url.title || url.metadata?.title}
            onClose={() => setShowVideoModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
